/**
 * Print Utility Functions
 * Reusable functions for printing forms and documents
 */

/**
 * Print a specific element by ID
 * @param {string} elementId - The ID of the element to print
 * @param {string} title - Optional title for the print window
 */
export const printElement = (elementId, title = 'Print') => {
  const printContent = document.getElementById(elementId);

  if (!printContent) {
    console.error(`Element with ID "${elementId}" not found`);
    return;
  }

  // Create a new window for printing
  const printWindow = window.open('', '_blank', 'width=800,height=600');

  if (!printWindow) {
    console.error('Failed to open print window. Please check popup blocker settings.');
    return;
  }

  // Get all stylesheets
  const styles = Array.from(document.styleSheets)
    .map((styleSheet) => {
      try {
        return Array.from(styleSheet.cssRules)
          .map((rule) => rule.cssText)
          .join('\n');
      } catch (e) {
        // Handle cross-origin stylesheets
        if (styleSheet.href) {
          return `@import url('${styleSheet.href}');`;
        }
        return '';
      }
    })
    .join('\n');

  // Build the print document
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>${title}</title>
        <style>
          ${styles}
          
          /* Additional print-specific styles */
          @media print {
            @page {
              size: A4;
              margin: 1cm;
            }
            
            body {
              margin: 0;
              padding: 0;
            }
            
            .no-print,
            button,
            .ant-btn,
            nav,
            .sidebar,
            .header,
            .breadcrumb {
              display: none !important;
            }
          }
        </style>
      </head>
      <body>
        ${printContent.innerHTML}
      </body>
    </html>
  `);

  printWindow.document.close();

  // Wait for content to load, then print
  printWindow.onload = () => {
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }, 250);
  };
};

/**
 * Print form data using PrintTemplate component
 * @param {Object} formData - The form data to print
 * @param {Array} sections - Section configuration
 * @param {Object} config - Print configuration
 */
export const printFormData = (formData, sections, config = {}) => {
  const {
    formTitle = 'Form',
    elementId = 'printable-area',
    title = 'Print',
  } = config;

  // Small delay to ensure React has rendered the print template
  setTimeout(() => {
    printElement(elementId, title);
  }, 100);
};

/**
 * Download form data as PDF (requires html2pdf library)
 * @param {string} elementId - The ID of the element to convert to PDF
 * @param {string} filename - The filename for the PDF
 */
export const downloadAsPDF = async (elementId, filename = 'document.pdf') => {
  try {
    // Check if html2pdf is available
    if (typeof window.html2pdf === 'undefined') {
      console.warn('html2pdf library not found. Please install: npm install html2pdf.js');
      // Fallback to print
      printElement(elementId, filename);
      return;
    }

    const element = document.getElementById(elementId);

    if (!element) {
      console.error(`Element with ID "${elementId}" not found`);
      return;
    }

    const opt = {
      margin: 10,
      filename: filename,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    };

    await window.html2pdf().set(opt).from(element).save();
  } catch (error) {
    console.error('Error generating PDF:', error);
    // Fallback to print
    printElement(elementId, filename);
  }
};

/**
 * Print current page
 */
export const printCurrentPage = () => {
  window.print();
};

/**
 * Create a printable version in a new tab or window
 * @param {string} elementId - The ID of the element to print
 * @param {string} title - Title for the new tab
 * @param {Object} options - { width, height, popup }
 */
export const openPrintPreview = (elementId, title = 'Print Preview', options = {}) => {
  const { width = 900, height = 700, popup = false } = options;
  const printContent = document.getElementById(elementId);

  if (!printContent) {
    console.error(`Element with ID "${elementId}" not found`);
    return;
  }

  const windowFeatures = popup
    ? `width=${width},height=${height},left=200,top=100,menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes`
    : '';

  const newTab = window.open('', '_blank', windowFeatures);

  if (!newTab) {
    console.error('Failed to open new tab. Please check popup blocker settings.');
    return;
  }

  // Get all stylesheets
  const styles = Array.from(document.styleSheets)
    .map((styleSheet) => {
      try {
        return Array.from(styleSheet.cssRules)
          .map((rule) => rule.cssText)
          .join('\n');
      } catch (e) {
        if (styleSheet.href) {
          return `@import url('${styleSheet.href}');`;
        }
        return '';
      }
    })
    .join('\n');

  newTab.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>${title}</title>
        <style>
          ${styles}
          
          body {
            margin: 20px;
            font-family: Arial, sans-serif;
          }
          
          .print-actions {
            position: fixed;
            top: 10px;
            right: 10px;
            z-index: 1000;
            background: white;
            padding: 10px;
            border-radius: 5px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.15);
          }
          
          .print-actions button {
            margin: 0 5px;
            padding: 8px 16px;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
          }
          
          .print-btn {
            background: #3498db;
            color: white;
          }
          
          .close-btn {
            background: #95a5a6;
            color: white;
          }
          
          @media print {
            .print-actions {
              display: none !important;
            }
          }
        </style>
      </head>
      <body>
        <div class="print-actions no-print">
          <button class="print-btn" onclick="window.print()">🖨️ Print</button>
          <button class="close-btn" onclick="window.close()">✖ Close</button>
        </div>
        ${printContent.innerHTML}
      </body>
    </html>
  `);

  newTab.document.close();
};

export default {
  printElement,
  printFormData,
  downloadAsPDF,
  printCurrentPage,
  openPrintPreview,
};
